import React from 'react';
import {ExclamationCircleFilled} from '@ant-design/icons';
import {Modal, Space, Tag, Tooltip} from 'antd';
import {Link} from 'react-router-dom';
import {
  cardPaymentStatusesConstants,
  invoiceTypeConstants,
  expenseTypesConstants,
  transactionTypesConstants,
} from '../../constants';
import {
  FileInvoiceEuroIcon,
  ProcessingIcon,
  ReceiptDeclinedIcon,
  ReceiptIcon,
  TransactionAddIcon,
  TransactionIcon,
  WireIcon,
  WithLessCreditCardIcon,
  WithLessOutsideIcon,
} from '../../icons';
import NameWithLink from '../../components/NameWithLink';
import UserDetails from '../../components/UserDetails';
import CreditCardDetails from '../../components/pages/CardsPage/CreditCardDetails';
import {amountHelpers} from './amount.helpers';
import {cardsHelpers} from './cards.helpers';
import {colorHelpers} from './color.helpers';
import {expensesHelpers} from './expenses.helpers';
import {objectHelpers} from './object.helpers';
import {textHelpers} from './text.helpers';

const {CARD_TRANSACTION} = transactionTypesConstants;

const {AUTHORIZATION_ACCEPTED, CLEARED, DECLINED, REFUND, REVERSED, SETTLED} = cardPaymentStatusesConstants;

const gMV = amountHelpers.getAmountWithCurrencyCode;

const cardPaymentStatuses = {
  [AUTHORIZATION_ACCEPTED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [CLEARED]: {
    key: 'cardPaymentStatuses.authorized',
  },
  [DECLINED]: {
    key: 'cardPaymentStatuses.declined',
    color: '#FF7875'
  },
  [REFUND]: {
    key: 'cardPaymentStatuses.refund',
  },
  [REVERSED]: {
    key: 'cardPaymentStatuses.reversed',
  },
  [SETTLED]: {
    key: 'cardPaymentStatuses.settled',
  },
};

export const transactionsHelpers = {
  additionalTransactionsGrouping: (transactions, summarizeKeyName) => transactions.map((t, key) => (
    (key !== 0 && key !== transactions.length - 1 && transactions[key + 1][summarizeKeyName])
      ? {...t, isLatest: true} : t)
  ),
  getAnimationStyles: ({
    defaultTableWidth = '100%',
    duration,
    detailsMaxWidth = 472
  } = {}) => {
    const transactionDetailsMaxWidth = `${detailsMaxWidth + 24}px`;
    const tableCollapseWidth = `calc(100% - ${transactionDetailsMaxWidth})`;
    const transactionDetailsHiddenRightPosition = `calc(-${transactionDetailsMaxWidth} - 10px)`;

    return {
      details: {
        default: {
          top: 0,
          position: 'absolute',
          transition: `all ${duration}ms ease-in-out`,
          width: transactionDetailsMaxWidth
        },
        entering: {opacity: 50, right: 0},
        entered: {opacity: 1, right: 0},
        exiting: {opacity: 50, right: transactionDetailsHiddenRightPosition},
        exited: {opacity: 0, right: transactionDetailsHiddenRightPosition}
      },
      table: {
        default: {
          transition: `width ${duration}ms ease-in-out`
        },
        entering: {width: tableCollapseWidth},
        entered: {width: tableCollapseWidth},
        exiting: {width: defaultTableWidth},
        exited: {width: defaultTableWidth}
      }
    }
  },
  isReturnedTransaction: (transaction) => transaction && [DECLINED, REVERSED].includes(transaction.payment_status),
  getTransactionAmount: ({transaction, variant, groupingKey = 'isMonthGrouping'}) => {
    if (transaction && typeof transaction === 'object') {
      let className = 'amount';
      let {amount} = transaction;
      if (transaction[groupingKey]) {
        className = 'total-amount';
        amount = transaction[variant] || 0;
        if (amount > 0 && variant === 'outcome') className = `${className} danger-text`;
        if (variant === 'outcome' && Math.sign(amount) > 0) amount = amount * -1;
        return <span className={className}>{gMV(amount)}</span>;
      } else {
        const isIncoming = transaction.is_incoming || false;
        const isPositiveAmount = Boolean(parseFloat(amount));
        if ((isIncoming && variant !== 'income') || (!isIncoming && variant !== 'outcome')) return '';
        const price = gMV(amount);
        const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
        if (!isIncoming && !isReturnedTransaction) className = `${className} danger-text`;
        if (isReturnedTransaction) className = `${className} returned-price`;
        return amount ? isIncoming ? price : <span className={className}>{isPositiveAmount && '-'}{gMV(Math.abs(amount))}</span> : null;
      }
    } else {
      return null
    }
  },

  isTransactionWithExpense: (transaction) => {
    const transactionType = transaction?.transaction_type;
    return transactionType === transactionTypesConstants.CARD_TRANSACTION
      || transactionType === transactionTypesConstants.PAYOUT;
  },

  getTransactionAuthor: (transaction) => (
    transaction?.user && transactionsHelpers.isTransactionWithExpense(transaction) ? (
      <UserDetails email={false} user={transaction.user} />
    ) : null
  ),

  getTransactionCardNumberWithLink: (transaction, symbolsLength = 5) => {
    if (!transaction) return null;

    const { card_id: cardId, user_id: userId } = transaction;
    const value = transactionsHelpers.getTransactionCardNumberWithName(transaction, symbolsLength);
    const className = `d-inline-flex row-link-container${cardId && userId ? ' active-link' : ''}`;

    if (cardId && userId) {
      const url = cardsHelpers.getCardDetailsUrl({ cardId, userId });
      return (
        <div className={className}>
          <Link onClick={(e) => e.stopPropagation()} to={url}>
            <NameWithLink name={value} url={url} variant='text' />
          </Link>
        </div>
      );
    }

    return <div className={className}>{value}</div>;
  },

  getTransactionCardNumberWithName: (transaction, symbolsLength = 5) => {
    const name = transaction?.card_name || '';
    const number = cardsHelpers.hideCardNumber(transaction?.masked_pan, symbolsLength) || '';
    return `${number} - ${name}`;
  },

  getTransactionSource: (transaction) => {
    let value = '';
    if (typeof transaction === 'object') {
      const description = objectHelpers.getObjProp(transaction, 'description');
      const source = objectHelpers.getObjProp(transaction, 'source');
      if (source) {
        value = source;
      } else if (description && description !== '') {
        value = textHelpers.cutString(description.split(' ')[0], 20)
      }
    }
    return value;
  },
  getTransactionVendor: (transaction) => {
    if (!transaction) return null;
    return  {
      name: transaction?.expense?.vendor_local_name  || transaction?.expense?.vendor,
      vendor_id: transaction?.expense?.vendor_local_id
    }
  },
  getTransactionSourceWithIcon: ({
    categories = [],
    enabledTooltip = true,
    transaction,
  }) => {
    if (transaction?.isMonthGrouping || transaction?.isYearlyGrouping) return ;

    let value = transactionsHelpers.getTransactionSource(transaction);
    const mccCode = transaction?.mcc_code;
    const category = (
      categories.find(c => c.mcc_code === mccCode) ||
      categories.find(c => objectHelpers.isNaV(c.mcc_code))
    )?.category;

    if (category) {
      const categoryIcon = expensesHelpers.getExpenseCategoryIcon(category?.id);
      value = (
        <Space align='center' className='d-flex'>
          <div className='d-flex'>
            {enabledTooltip ? (
              <Tooltip
                title={category?.name || ''}
                trigger='hover'
              >
                <div className='d-flex'>{categoryIcon}</div>
              </Tooltip>
            ): categoryIcon}
          </div>
          <div>{value}</div>
        </Space>
      );
    }
    return value;
  },
  getTransactionStatus: (status) => {
    const statuses = Object.fromEntries(
      Object.entries(cardPaymentStatuses).map(([key, value]) => [
        key,
        { ...value, bgColor: value?.color ? colorHelpers.hex2rgba(value.color, 0.20) : '#F3F3F4' },
      ])
    );

    return objectHelpers.getStatus(status, statuses);
  },
  getTransactionType: (transaction) => {
    if (transaction && typeof transaction === 'object') {
      const transactionType = transaction.transaction_type;
      let element = <WireIcon />;
      if (transactionType === CARD_TRANSACTION) {
        element = (
          <CreditCardDetails
            cardNumber={transaction.masked_pan}
            cardNumberClassName='card-number'
          />
        );
      }
      return <span className='transaction-type'>{element}</span>;
    } else {
      return null;
    }
  },
  getTransactionTypeIcon: (transaction, isOutside = false) => {
    if (isOutside) return <WithLessOutsideIcon />;
    if (!transaction) return null;
    return transaction.transaction_type === CARD_TRANSACTION ? <WithLessCreditCardIcon /> : <WireIcon />
  },
  getTransactionPaymentMethodDetails: (transaction) => {
    if (!transaction) return null;

    const isCardTransaction = transaction.transaction_type === CARD_TRANSACTION;
    const icon = transactionsHelpers.getTransactionTypeIcon(transaction);
    const value = isCardTransaction ? transactionsHelpers.getTransactionCardNumberWithLink(transaction) : '';
    return (
      <Space align='center' size={4} className='transaction-type d-flex-child'>
        {icon}<span>{value}</span>
      </Space>
    );
  },
  getTransactionAttachmentIcon: ({ transaction, translation, ...rest }) => {
    if (!transaction?.expense) return null;

    const { onClick } = rest;
    const { expense_type } = transaction.expense;
    const amount = parseFloat(transaction?.amount);
    const paymentStatus = transaction.payment_status;
    const isDefinedExpenseType = objectHelpers.isValidValue(expense_type);
    const isDeclined = paymentStatus === cardPaymentStatusesConstants.DECLINED;
    const isProcessing = [cardPaymentStatusesConstants.AUTHORIZATION_ACCEPTED, cardPaymentStatusesConstants.CLEARED].includes(paymentStatus);

    if (isDeclined || !amount) return null;

    const icon =
      Object.values(expenseTypesConstants).includes(expense_type)
        ? <TransactionIcon />
        : <TransactionAddIcon />;

    return (
      <Space align={4} className='space-flex flex-content-end'>
        {isProcessing && (
          <Tooltip placement='left' title={translation('main:processing')}>
            <span className='transaction-icon'>
              <ProcessingIcon />
            </span>
          </Tooltip>
        )}
        <span
          className='transaction-icon'
          {...rest}
          onClick={(e) => !isDefinedExpenseType && onClick?.({ e, transaction })}
          style={{ cursor: isDefinedExpenseType ? 'default' : 'pointer' }}
        >
          {icon}
        </span>
      </Space>
    );
  },
  getWireDetails: (details) => {
    const gObjProp = (propName) => objectHelpers.getObjProp(details, propName);
    const getFormattedValue = (value) => value ? value.match(/.{1,4}/g).join(' ') : '';

    return {
      bic: getFormattedValue(gObjProp('bic')),
      iban: getFormattedValue(gObjProp('iban'))
    };
  },
  getInvoiceEmail: (settings) => {
    let email = null;
    const gObjProp = (propName) => objectHelpers.getObjProp(settings, propName);

    if (settings) email = gObjProp('invoice_email_alias') || gObjProp('invoice_email') || null;
    return email;
  },
  addFixedDetailsWindowScrollEventListener: ({detailsWindowFixedHeight, isOpenDetails, isFixedDetailsWindow, setIsFixedDetailsWindow}) => {
    const setScroll = (e) => {
      let scrolled = false;
      if (!isOpenDetails) return;
      if (e.target.scrollTop >= detailsWindowFixedHeight) scrolled = true;
      if (isFixedDetailsWindow !== scrolled) setIsFixedDetailsWindow(scrolled);
    };
    document.querySelector('#app-page-container')?.addEventListener('scroll', setScroll);
    return () => {
      document.querySelector('#app-page-container')?.removeEventListener('scroll', setScroll);
    };
  },
  getGroupedTransactions: (transactions, totals = []) => {
    const daysList = {};
    const datePropName = 'created_date';
    const summarizeKeyName = 'isMonthGrouping';
    let groupedTransactions = [];
    let lastAdded;

    // define transactions months
    transactions.forEach(transaction => {
      const date = transaction[datePropName].slice(0, 7);
      const {is_incoming: isIncoming} = transaction;
      const amount = Number(transaction.amount) || 0;
      const income = isIncoming ? amount : 0;
      const outcome = isIncoming ? 0 : amount;
      const isReturnedTransaction = transactionsHelpers.isReturnedTransaction(transaction);
      if (!isReturnedTransaction) {
        if (daysList.hasOwnProperty(date)) {
          daysList[date] = {
            income: daysList[date].income + income,
            outcome: daysList[date].outcome + outcome
          }
        } else {
          daysList[date] = {
            income,
            outcome
          }
        }
      }
    });

    // add grouping rows to transactions list
    transactions.forEach(t => {
      const date = t[datePropName].slice(0, 7);
      if (lastAdded !== date && daysList.hasOwnProperty(date)) {
        let groupedTransaction = {
          id: date,
          [summarizeKeyName]: true,
          [datePropName]: date,
          ...daysList[date]
        }
        const totalByDate = totals.find(t => `${t.year}-${t.month}` === date);
        if (totalByDate) {
          groupedTransaction = {
            ...groupedTransaction,
            income: totalByDate?.total_in,
            outcome: totalByDate?.total_out
          }
        }
        lastAdded = date;
        groupedTransactions.push(groupedTransaction);
      }
      groupedTransactions.push(t);
    });
    groupedTransactions = transactionsHelpers.additionalTransactionsGrouping(groupedTransactions, summarizeKeyName);
    return groupedTransactions;
  },
  getInvoiceTypeOptions: ({t, enabledEmpty = false}) => {
    let options = [
      {
        icon: <FileInvoiceEuroIcon />,
        label: t(`invoiceTypes.invoice`),
        value: invoiceTypeConstants.INVOICE
      },
      {
        icon: <ReceiptIcon />,
        label: t(`invoiceTypes.receipt`),
        value: invoiceTypeConstants.RECEIPT
      }
    ];
    if (enabledEmpty) {
      options = [
        ...options,
        {
          icon: <ReceiptDeclinedIcon />,
          label: t(`invoiceTypes.noReceipt`),
          value: invoiceTypeConstants.NO_RECEIPT
        }
      ];
    }
    return options;
  },
  getExpenseTypeOptions: ({t}) => {
    return [
      {
        label: t(`expenseTypes.invoice`),
        value: expenseTypesConstants.INVOICE
      },
      {
        label: t(`expenseTypes.receipt`),
        value: expenseTypesConstants.RECEIPT
      },
      {
        label: t(`expenseTypes.noReceipt`),
        value: expenseTypesConstants.NO_RECEIPT
      }
    ];
  },
  isMonthlyGroupedRow: (row) => row.isMonthGrouping,
  getTransactionsWithExpense: ({employees, expenses, transactions}) => {
    return transactions.map(t => {
      const {expense_id: expenseId, user_id: userId} = t;
      const user = employees.find(e => e.employee_id === userId);
      const expense = expenses.find(e => e.id === expenseId);
      return {
        ...t,
        expense,
        user
      }
    });
  },
  getRowClassName: ({
    groupedPropName,
    index,
    record,
    selectedRowIndex
  }) => {
    let className = '';
    if (record[groupedPropName]) className += ' grouped-row';
    if (index === selectedRowIndex) className += ' selected-row';
    return className;
  },
  getUpdatedBatchExpensesList: (expenses, updatedExpense) =>
    expenses.map(expense => expense.id === updatedExpense.id ? {...expense, ...updatedExpense} : expense),
  removeInvoiceFromTransaction: ({
    t,
    transaction,
    successCallback,
    errorCallback,
    unlinkExpense,
    onExpenseUpdate
  }) => {
    if (transaction.expense) {
      Modal.confirm({
        title: t('transactions:modal.removeInvoice.title'),
        icon: <ExclamationCircleFilled />,
        content: t('transactions:modal.removeInvoice.description'),
        okText: t('main:yes'),
        okType: 'danger',
        cancelText: t('main:no'),
        cancelButtonProps: {size: 'large'},
        okButtonProps: {size: 'large'},
        onCancel() {
          errorCallback && errorCallback();
        },
        onOk() {
          const {expense} = transaction;
          unlinkExpense({
            data: {expense_id: expense.id},
            successCallback: () => {
              onExpenseUpdate({
                ...expense,
                expense_type: null,
                expense_invoice_status: undefined,
              });
              successCallback?.();
            },
            errorCallback: (resp) => errorCallback && errorCallback(resp)
          })
        }
      });
    } else {
      errorCallback && errorCallback();
    }
  },
  getTransactionInvoiceType: (transaction) => {
    let expense = transaction?.expense;
    if (!expense) return undefined;

    return expense?.subscription_id ? invoiceTypeConstants.INVOICE : invoiceTypeConstants.RECEIPT;
  },
  getDeclinedTransactionStatuses: () => [cardPaymentStatusesConstants.DECLINED, cardPaymentStatusesConstants.REVERSED, cardPaymentStatusesConstants.REFUND],
  getTransactionDetailsRightContent: ({transaction, t}) => {
    const paymentStatus = transaction?.payment_status;
    if (!paymentStatus) return null;

    const status = transactionsHelpers.getTransactionStatus(paymentStatus);

    const tagStyle = {
      backgroundColor: status.bgColor || 'unset',
      borderColor: status.color,
      color: status.color,
      margin: 0
    };

    let tagName = t(status.key);
    const authorizationNote = transaction?.authorization_note;
    const isDeclined = paymentStatus === cardPaymentStatusesConstants.DECLINED;

    if (isDeclined && authorizationNote) tagName = `${tagName}: ${authorizationNote}`;

    const tagElement = (
      <Tag style={tagStyle}>
        {textHelpers.cutString(tagName, 22)}
      </Tag>
    );

    return (
      <div style={{ marginLeft: 'auto', fontSize: 12, maxWidth: 215 }}>
        {isDeclined ? <Tooltip title={tagName}>{tagName}</Tooltip> : tagElement}
      </div>
    );
  }

}
